import {
  Form,
  Modal,
  ModalProps,
  Select,
  Spacing,
  Typography,
} from "@p0security/ui";
import { useMonitors } from "components/Assessment/contexts/MonitorsContext";
import React, { useCallback, useMemo, useState } from "react";

import { EditMonitorForm } from "./EditMonitorForm";
import { CreateMonitorModalProps } from "./types";

export const CreateMonitorModalContent: React.FC<
  CreateMonitorModalProps & {
    modalProps: Partial<ModalProps>;
  }
> = (props) => {
  const {
    open,
    onCancel,
    initialSearchTerm,
    show,
    templateValues,
    templateKey,
    allowTemplateSelection,
    startWithTemplate,
    modalProps,
  } = props;
  const { monitors } = useMonitors();
  const [selectedTemplateId, setSelectedTemplateId] = useState<
    string | undefined
  >(startWithTemplate ? "" : undefined);

  const templateSelectionEnabled = Boolean(allowTemplateSelection);

  const templateMonitorOptions = useMemo(() => {
    if (!templateSelectionEnabled || !monitors) return [];
    return Object.entries(monitors)
      .filter(([_id, m]) => !m.isCustom)
      .map(([id, m]) => ({ value: id, label: m.label }));
  }, [monitors, templateSelectionEnabled]);

  const handleTemplateSelect = useCallback(
    (templateId: string | undefined) => {
      if (!templateSelectionEnabled) return;
      setSelectedTemplateId(templateId);
    },
    [templateSelectionEnabled]
  );

  const selectedTemplate = useMemo(() => {
    if (
      !selectedTemplateId ||
      selectedTemplateId === "" ||
      monitors === undefined
    )
      return undefined;
    return monitors[selectedTemplateId];
  }, [monitors, selectedTemplateId]);

  // Add "Copy of" prefix to template values when creating from a template
  const effectiveTemplateValues = useMemo(() => {
    if (!templateSelectionEnabled) {
      // If we have template values passed in, add "Copy of" prefix to the label
      if (
        templateValues?.label &&
        !templateValues.label.startsWith("Copy of ")
      ) {
        return {
          ...templateValues,
          label: `Copy of ${templateValues.label}`,
        };
      }
      return templateValues;
    }

    // If a template is selected, add "Copy of" prefix to its label
    if (selectedTemplate) {
      return {
        ...selectedTemplate,
        label: `Copy of ${selectedTemplate.label}`,
      };
    }

    return templateValues;
  }, [templateSelectionEnabled, selectedTemplate, templateValues]);

  const effectiveTemplateKey = useMemo(() => {
    if (!templateSelectionEnabled) return templateKey;
    return selectedTemplateId;
  }, [templateSelectionEnabled, selectedTemplateId, templateKey]);

  return (
    <Modal footer={false} onCancel={onCancel} open={open} {...modalProps}>
      <Typography.Title level={4}>Create New Monitor</Typography.Title>
      {templateSelectionEnabled && templateMonitorOptions.length > 0 && (
        <Spacing asChild mb={16}>
          <Form.Item
            label="Use Template"
            labelCol={{ span: 8 }}
            wrapperCol={{ span: 16 }}
          >
            <Select
              placeholder="Select a preset monitor as template (optional)"
              allowClear
              value={selectedTemplateId === "" ? undefined : selectedTemplateId}
              onChange={handleTemplateSelect}
              options={templateMonitorOptions}
              autoFocus={startWithTemplate}
            />
          </Form.Item>
        </Spacing>
      )}
      <EditMonitorForm
        onSubmitForm={onCancel}
        initialSearchTerm={initialSearchTerm}
        show={show}
        isCustom={true}
        templateValues={effectiveTemplateValues}
        templateKey={effectiveTemplateKey}
      />
    </Modal>
  );
};
